import { Injectable, Renderer2 } from '@angular/core';
import { LegHelperService } from './leg-helper.service';
import { LegScheduleService } from './leg-schedule.service';
import { setupCurrencyWhenCrossChanges } from '../utilities/cross-utilities';
import {
  AdminService,
  PricingExtraService,
  ProductsConfigService
} from '@approot/services';
import { optionsChangeHandler } from '../utilities/leg.utilities';
import { getAllAttributeOptions } from '../utilities/leg-attribute-utilities';
import { DateUtilities, Utilities } from '@approot/utilities';
import { LegDateService } from './leg-date.service';
import { setDefaultCutOffFixingSource } from '../utilities/cutoff-fixing-source.utilities';
import { Leg, LegSyncingFields } from '@approot/models';
import { LegSpotStrikeService } from './leg-spot-strike.service';
import { AbstractControl, FormGroup } from '@angular/forms';
import { LegMiscellaneousService } from './leg-miscellaneous.service';

@Injectable({
  providedIn: 'root'
})
export class LegCurrencyService {
  constructor(
    private legHelperService: LegHelperService,
    private legScheduleService: LegScheduleService,
    private adminService: AdminService,
    private renderer: Renderer2,
    private legDateService: LegDateService,
    private productsConfigService: ProductsConfigService,
    private pricingExtraService: PricingExtraService,
    private legSpotStrikeService: LegSpotStrikeService,
    private legMiscellaneousService: LegMiscellaneousService
  ) {}

  public onCurrencyChange(val: string): void {
    // if currency changes add value to end of text box
    const form = this.legHelperService.form;
    const leg = this.legHelperService.leg;
    const rfqExecutionSettings = this.legHelperService.rfqExecutionSettings;

    this.legScheduleService.callSwapScheduleGenerator('fixedLegSchedule');
    this.legScheduleService.callSwapScheduleGenerator('floatingLegSchedule');
    if (
      !form.get('cross') &&
      !form.get('secondCross') &&
      !rfqExecutionSettings.directRfq
    ) {
      setupCurrencyWhenCrossChanges(
        form,
        leg,
        this.legHelperService.presetsLoaded,
        this.adminService.useDefaults,
        this.pricingExtraService.currencies
      );
    }

    if (!form.get('cross')) {
      if (val) {
        form.get('expiryDate')?.enable();
        form.get('settlementDate')?.enable();
      } else {
        form.get('expiryDate')?.disable();
        form.get('settlementDate')?.disable();
      }
    }

    if (
      leg.productType === 'choosertpf' ||
      leg.productType === 'frozenchoosertpf' ||
      leg.productType === 'chooserpivottpf'
    ) {
      if (form.get('targetProfitUnit')) {
        optionsChangeHandler(
          form,
          leg,
          'targetProfitUnit',
          ['Common Currency'],
          'Common Currency'
        );
      }
    } // If a chooser tarf, after selecting currency, set TP Unit to Common Currency

    if (leg.productType === 'simpletarf') {
      if (form.get('targetProfitUnit')) {
        optionsChangeHandler(form, leg, 'targetProfitUnit', ['Spot'], 'Spot');
        form.get('targetProfitUnit').setValue('Spot');
      }
    }

    if (val) {
      this.onCurrencyChangeWithVal(val, form, leg);
    } else {
      this.onCurrencyChangeWithNoVal(form);
    }
  }

  private onCurrencyChangeWithNoVal(form: FormGroup): void {
    // we want to remove the currency from the textbox
    if (form.get('notional')) {
      let textToRemove = ' XXX';
      if (form.get('schedule')) {
        // we want to add per fixing to the end of the textbox if there is a schedule
        textToRemove = textToRemove + ' per fixing';
      }

      Utilities.endOfTextBoxAdder(
        this.legHelperService.qs('#' + 'notional'),
        form.get('notional'),
        textToRemove,
        false,
        'currency',
        this.renderer
      );
    }

    ['notionalLong', 'notionalShort', 'callNotional', 'putNotional'].forEach(
      key => {
        if (form.get(key)) {
          Utilities.endOfTextBoxAdder(
            this.legHelperService.qs('#' + key),
            form.get(key),
            ' XXX',
            false,
            'currency',
            this.renderer
          );
        }
      }
    );

    if (!form.get('cross') && form.get('settlementDate')) {
      // if no cross but there is a settlement date disable the settlement date
      form.get('settlementDate').disable();
    }
  }

  private onCurrencyChangeWithVal(
    val: string,
    form: FormGroup,
    leg: Leg
  ): void {
    if (
      form.get('targetProfitUnit') &&
      form.get('targetProfitUnit').value !== 'None' &&
      form.get('targetProfitUnit').value !== 'Spot'
    ) {
      if (form.get('targetProfitAdjustmentType')) {
        if (form.get('targetProfitAdjustmentType').value !== 'None') {
          // If currency changes, change options in TP Adjustment

          let tpAdjCurr = [...getAllAttributeOptions('currency', leg)];
          optionsChangeHandler(
            form,
            leg,
            'targetProfitAdjustmentType',
            [
              'Adjust Strike',
              'Adjust Notional',
              'Cash (' + tpAdjCurr[0] + ')',
              'Cash (' + tpAdjCurr[1] + ')'
            ],
            form.get('targetProfitAdjustmentType').value
          );
        }
      }
    }

    let textToAdd = ' ' + val;
    if (form.get('schedule')) {
      // we want to add per fixing to the end of the textbox if there is a schedule
      textToAdd = textToAdd + ' per fixing';
    }

    [
      'notional',
      'notionalLong',
      'notionalShort',
      'callNotional',
      'putNotional'
    ].forEach(key => {
      if (form.get(key)) {
        Utilities.endOfTextBoxAdder(
          this.legHelperService.qs('#' + key),
          form.get(key),
          key === 'notional' ? textToAdd : ' ' + val,
          !!val,
          'currency',
          this.renderer
        );
      }
    });
    if (!form.get('cross') && form.get('settlementDate')) {
      // if no cross but there is a settlement date enable the settlement date
      form.get('settlementDate').enable({ emitEvent: false });
    }
    if (!form.get('cross')) this.legDateService.setPremiumDefaultDate(); // if no cross setup premium payment date
  }

  public onDepositCurrencyChange(val: string): void {
    const leg = this.legHelperService.leg;
    const form = this.legHelperService.form;
    leg.depositCurrency = val;
    if (val) {
      this.depositCurrencyChangeAltMargin(val); // if deposit currency changes then change option of alternative currencies
      if (form.get('depositDate')) {
        form.get('depositDate').enable();
      }

      // change in deposit currency should add the value to end of notional text box.
      let textToAdd = ' ' + val;
      if (form.get('schedule')) {
        // we want to add per fixing to the end of the textbox if there is a schedule
        textToAdd = textToAdd + ' per fixing';
      }
      Utilities.endOfTextBoxAdder(
        this.legHelperService.qs('#' + 'notional'),
        form.get('notional'),
        textToAdd,
        !!val,
        'currency',
        this.renderer
      );
    } else {
      this.depositCurrencyChangeAltMargin('');
      form.get('alternativeCurrency').setValue('');
      if (form.get('depositDate')) {
        form.get('depositDate').setValue('');
        form.get('depositDate').disable();
      }
      form.get('redemptionDate').disable();

      let textToAdd = ' XXX';
      if (form.get('schedule')) {
        // we want to add per fixing to the end of the textbox if there is a schedule
        textToAdd = textToAdd + ' per fixing';
      }

      Utilities.endOfTextBoxAdder(
        this.legHelperService.qs('#' + 'notional'),
        form.get('notional'),
        textToAdd,
        false,
        'currency',
        this.renderer
      ); // remove currency from text box
    }
  }

  private depositCurrencyChangeAltMargin(currency: string) {
    // load alt currencies options
    const leg = this.legHelperService.leg;
    const form = this.legHelperService.form;
    if (form.get('depositDate')) {
      form.get('depositDate').setValue('');
    }

    optionsChangeHandler(
      form,
      leg,
      'alternativeCurrency',
      this.productsConfigService.productInfo.currencyPairs
        .filter(x => {
          const base = x.substring(0, 3);
          const quote = x.substring(3, 6);
          return base === currency || quote === currency;
        })
        .map(y => y.replace(currency, '')),
      ''
    );

    if (
      form.get('marginType')?.value === 'Deposit Currency' &&
      form.get('margin').value
    ) {
      // update the margin deposit currency
      Utilities.endOfTextBoxAdder(
        this.legHelperService.qs('#' + 'margin'),
        form.get('margin'),
        ' ' + currency,
        !!currency,
        'currency',
        this.renderer
      );
    }

    if (!form.get('cross') && !form.get('secondCross')) {
      setupCurrencyWhenCrossChanges(
        form,
        leg,
        this.legHelperService.presetsLoaded,
        this.adminService.useDefaults,
        this.pricingExtraService.currencies
      );
    }
  }

  public onAlternativeCurrencyChange(
    val: string,
    legSyncingInfo: LegSyncingFields,
    legIndex: number,
    hasSpotAlreadyLoaded: boolean,
    spotCtrl: AbstractControl
  ): void {
    const form = this.legHelperService.form;
    const leg = this.legHelperService.leg;
    const rfqExecutionSettings = this.legHelperService.rfqExecutionSettings;
    if (!form.get('cross') && !form.get('secondCross')) {
      setupCurrencyWhenCrossChanges(
        form,
        leg,
        this.legHelperService.presetsLoaded,
        this.adminService.useDefaults,
        this.pricingExtraService.currencies
      );
    }
    leg.alternativeCurrency = val;
    leg.usableCross = this.productsConfigService.getCrossFromCurrencies(
      form.get('depositCurrency')?.value,
      val
    );

    if (legSyncingInfo?.spot) {
      this.legSpotStrikeService.subToSpotChanges(legIndex);
    }
    form.get('cutOff').setValue(''); // for cutoff there is no selected currency pair
    if (form.get('treasuryRate')) {
      form.get('treasuryRate').setValue('');
    }
    if (val) {
      const createdCross = this.productsConfigService.getCrossFromCurrencies(
        form.get('depositCurrency').value,
        val
      );

      if (form.get('startDate')) {
        form.get('startDate').enable({ emitEvent: false }); // enable the start date
        // make make start date be 2b (0b but taking the settlement date)
        if (
          form.get('depositCurrency').value &&
          this.legHelperService.presetsLoaded &&
          createdCross
        ) {
          const customPayload = DateUtilities.dateOrLagToPayload(
            '0b',
            createdCross,
            this.pricingExtraService.getMarketDataTimeStamp,
            this.productsConfigService.getCutOffCode(form.get('cutOff')?.value),
            this.productsConfigService.getFixingSourceCode(
              form.get('fixingSource')?.value
            )
          );
          this.legDateService.calculateExpirationSettlementDate(
            null,
            {
              formControl: form.get('startDate'),
              setFormControl: true,
              controlName: 'startDate',
              useDate: true
            },
            '',
            customPayload
          );
        }
      }
      // this is needed as to find the cutoff from the currency pair
      // one of USDAUD or AUDUSD will be in the JSON. Both are the same

      if (createdCross && val && this.legHelperService.presetsLoaded) {
        // is there a cross
        this.legMiscellaneousService.fixingSourcesCutOffsFetcher(form, leg);
      } else {
        optionsChangeHandler(form, leg, 'cutOff', [], '');
      }

      let shouldSpotEmitEvent = true;

      if (!hasSpotAlreadyLoaded && !leg.newProduct) {
        // we dont want to emit changes on initial spot auto call
        shouldSpotEmitEvent = false;
      }

      if (spotCtrl && createdCross && !rfqExecutionSettings.static) {
        // is there a spot and a cross in the currencyPairs downloaded
        spotCtrl.setValue('');
        this.legSpotStrikeService.spotCaller(
          createdCross,
          'spot',
          shouldSpotEmitEvent
        );
      }
    } else {
      // wipe all to do with the currencies
      optionsChangeHandler(form, leg, 'cutOff', [], ''); // no currency pair, wipe cutoffs options
      form.get('redemptionDate')?.disable();
      form.get('expiryDate')?.disable();
      form.get('startDate')?.disable();
    }
  }

  public reFormatDecimalsOnCurrencyChange(): void {
    // goes through and reformats the attributes whose format is reliant on the cross
    const form = this.legHelperService.form;
    Object.keys(form.value).forEach((key: string) => {
      // loop through all keys and reformat the data
      this.legHelperService.decimalCurrencyFormatter(
        key,
        Utilities.attributeBelongsToWhichCross(key)
      );
    });
  }
}
